

import React, { useState, useEffect, useRef } from 'react';
import apiService from '../Service/apiService';
import webSocketService from '../Service/webSocketService';
import './PromotionDeta.css';

const PromotionDeta = ({ data, deviceInfo, onLogout }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [nextCampaign, setNextCampaign] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showBackButton, setShowBackButton] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [wakeLockEnabled, setWakeLockEnabled] = useState(false);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const nextVideoRef = useRef(null);
  const hideButtonTimerRef = useRef(null);
  const wakeLockRef = useRef(null);
  const preloadTimerRef = useRef(null);

  // Request wake lock to prevent screen from sleeping
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        if (wakeLockRef.current) {
          await wakeLockRef.current.release();
        }
        
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        setWakeLockEnabled(true);
        
        wakeLockRef.current.addEventListener('release', () => {
          setWakeLockEnabled(false);
          
          if (document.visibilityState === 'visible') {
            //console.log('🔄 Attempting to reacquire wake lock...');
            requestWakeLock();
          }
        });
      } else {
        //console.log('⚠️ Wake Lock API not supported in this browser');
      }
    } catch (err) {
      console.error('❌ Failed to activate Wake Lock:', err);
      setWakeLockEnabled(false);
    }
  };

  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        setWakeLockEnabled(false);
        //console.log('🔌 Wake Lock released');
      } catch (err) {
        console.error('❌ Failed to release Wake Lock:', err);
      }
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        //console.log('📱 Page became visible, ensuring wake lock is active');
        if (!wakeLockRef.current) {
          requestWakeLock();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    requestWakeLock();
    
    const wakeLockInterval = setInterval(() => {
      if (wakeLockRef.current) {
        //console.log('🔄 Refreshing wake lock...');
        requestWakeLock();
      }
    }, 30000);
    
    return () => {
      clearInterval(wakeLockInterval);
      releaseWakeLock();
    };
  }, []);

  // Connect to WebSocket on component mount
  useEffect(() => {
    if (deviceInfo?.deviceId) {
      //console.log('🔌 Establishing WebSocket connection for device:', deviceInfo.deviceId);
      
      webSocketService.connect(deviceInfo.deviceId);

      const handleWsMessage = (event) => {
        const { topic, data } = event.detail;
        //console.log(`📩 WebSocket message on ${topic}:`, data);
        
        if (data.type === 'CAMPAIGN_UPDATE' || data.campaignId) {
          handleCampaignUpdate(data);
        }
      };

      window.addEventListener('ws-message', handleWsMessage);

      const interval = setInterval(() => {
        setWsConnected(webSocketService.isConnected());
      }, 1000);

      return () => {
        //console.log('🧹 Cleaning up WebSocket connection');
        window.removeEventListener('ws-message', handleWsMessage);
        clearInterval(interval);
        webSocketService.disconnect();
      };
    }
  }, [deviceInfo]);

  const handleCampaignUpdate = (updatedCampaign) => {
    //console.log('🔄 Campaign update received:', updatedCampaign);
    
    setCampaigns(prevCampaigns => {
      const newCampaigns = prevCampaigns.map(c => 
        c.campaignId === updatedCampaign.campaignId 
          ? { ...c, ...updatedCampaign } 
          : c
      );
      
      const newPlaylist = apiService.generatePlaylist(newCampaigns);
      setPlaylist(newPlaylist);
      
      return newCampaigns;
    });

    if (currentCampaign && currentCampaign.campaignId === updatedCampaign.campaignId) {
      setCurrentCampaign(prev => ({ ...prev, ...updatedCampaign }));
    }
  };

  useEffect(() => {
    if (data && data.campaigns) {
      const activeCampaigns = data.campaigns.filter(
        campaign => campaign.medium === "DIGITAL" && campaign.status === "ACTIVE"
      );

      //console.log('Active campaigns:', activeCampaigns);
      setCampaigns(activeCampaigns);
      
      const newPlaylist = apiService.generatePlaylist(activeCampaigns);
      setPlaylist(newPlaylist);
      
      if (newPlaylist.length > 0) {
        // Start with first campaign - no loading screen
        setCurrentCampaign(newPlaylist[0]);
        // Preload next campaign
        preloadNextCampaign(1, newPlaylist);
      }
    }
  }, [data]);

  // Preload next campaign
  const preloadNextCampaign = (startIndex, currentPlaylist = playlist) => {
    if (currentPlaylist.length === 0) return;
    
    const nextIndex = (startIndex) % currentPlaylist.length;
    const next = currentPlaylist[nextIndex];
    
    if (next) {
      //console.log(`🔄 Preloading next campaign: ${next.campaignId}`);
      setNextCampaign(next);
      
      // Preload image or video
      if (next.advertisementType === 'IMAGE') {
        const img = new Image();
        img.src = next.imageUrl;
      } else {
        // For video, we'll preload via hidden video element
        if (nextVideoRef.current) {
          nextVideoRef.current.src = next.imageUrl;
          nextVideoRef.current.load();
        }
      }
    }
  };

  useEffect(() => {
    const handleMouseMove = () => {
      setShowBackButton(true);
      if (hideButtonTimerRef.current) {
        clearTimeout(hideButtonTimerRef.current);
      }
      hideButtonTimerRef.current = setTimeout(() => {
        setShowBackButton(false);
      }, 5000);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (hideButtonTimerRef.current) {
        clearTimeout(hideButtonTimerRef.current);
      }
    };
  }, []);

  // ✅ ORIGINAL PUT API LOGIC PRESERVED - just removed loading state from UI
  const activateCampaign = async (campaign) => {
    if (!campaign) return;
    
    try {
      //console.log(`Activating campaign ${campaign.campaignId} (${campaign.priority})`);
      // ✅ PUT API CALL - exactly as before, just not showing loading spinner
      await apiService.updateCampaignActive(campaign.campaignId, data.deviceId, true);
      
      // Switch to next campaign AFTER successful API call
      setCurrentCampaign(campaign);
      
      // Preload the next campaign
      const nextIndex = (playlist.findIndex(c => c.campaignId === campaign.campaignId) + 1) % playlist.length;
      preloadNextCampaign(nextIndex);
      
    } catch (error) {
      console.error('Error activating campaign:', error);
    }
  };

  const playNext = () => {
    if (playlist.length === 0) return;
    
    // Use preloaded next campaign if available
    if (nextCampaign) {
      //console.log(`➡️ Switching to preloaded campaign: ${nextCampaign.campaignId}`);
      activateCampaign(nextCampaign); // ✅ PUT API called here
    } else {
      // Fallback if no preloaded campaign
      const nextIndex = (currentIndex + 1) % playlist.length;
      activateCampaign(playlist[nextIndex]); // ✅ PUT API called here
    }
  };

  const startPlaybackTimer = (duration) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // Preload next campaign halfway through current one
    if (preloadTimerRef.current) {
      clearTimeout(preloadTimerRef.current);
    }
    
    // Preload at 70% of duration
    const preloadTime = Math.max(duration * 0.7, 1000);
    preloadTimerRef.current = setTimeout(() => {
      const nextIndex = (playlist.findIndex(c => c.campaignId === currentCampaign?.campaignId) + 1) % playlist.length;
      preloadNextCampaign(nextIndex);
    }, preloadTime);
    
    timerRef.current = setTimeout(() => {
      //console.log(`Campaign ${currentCampaign?.campaignId} finished playing`);
      playNext();
    }, duration);
  };

  const handleVideoEnd = () => {
    //console.log(`Video ended for campaign ${currentCampaign?.campaignId}`);
    playNext();
  };

  const handleBackClick = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (preloadTimerRef.current) clearTimeout(preloadTimerRef.current);
    if (hideButtonTimerRef.current) clearTimeout(hideButtonTimerRef.current);
    releaseWakeLock();
    webSocketService.disconnect();
    onLogout();
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (preloadTimerRef.current) clearTimeout(preloadTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (playlist.length > 0 && currentCampaign) {
      const index = playlist.findIndex(c => c.campaignId === currentCampaign.campaignId);
      if (index !== -1) setCurrentIndex(index);
    }
  }, [playlist, currentCampaign]);

  if (!currentCampaign) {
    return (
      <div className="promotions-deta">
        <div className="no-campaigns">
          <h2>No Active Campaigns</h2>
          <p>No digital campaigns available to display</p>
          <button onClick={onLogout} className="back-btn">Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="promotions-deta">
      {/* Hidden video element for preloading next video */}
      <video
        ref={nextVideoRef}
        style={{ display: 'none' }}
        muted
        preload="auto"
      />

      {/* Back Button */}
      <button 
        className={`back-button ${showBackButton ? 'visible' : ''}`}
        onClick={handleBackClick}
        aria-label="Go back to login"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        {/* <span>Back</span> */}
      </button>

      <div className="display-area">
        {/* No loading spinner - always show media */}
        <div className="media-player">
          {currentCampaign.advertisementType === 'IMAGE' ? (
            <img 
              key={currentCampaign.campaignId}
              src={currentCampaign.imageUrl} 
              alt={currentCampaign.campaignName}
              className="campaign-media"
              onLoad={() => {
                //console.log(`Image loaded for campaign ${currentCampaign.campaignId}, playing for ${currentCampaign.durationSeconds}s`);
                startPlaybackTimer(currentCampaign.durationSeconds * 1000);
              }}
            />
          ) : (
            <video
              key={currentCampaign.campaignId}
              ref={videoRef}
              src={currentCampaign.imageUrl}
              className="campaign-media"
              autoPlay
              // muted
              onEnded={handleVideoEnd}
              onLoadedMetadata={() => {
                //console.log(`Video loaded for campaign ${currentCampaign.campaignId}`);
                startPlaybackTimer(currentCampaign.durationSeconds * 1000);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PromotionDeta;